import {
  readZipEntries,
  readZipEntryText,
  readZipEntryBody,
  writeZip,
  crc32,
} from "@amoreg/shared/zip";
const WORD = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const MAX_XML = 4 * 1024 * 1024;
export interface DocxParagraph {
  index: number;
  text: string;
  editable: boolean;
}
export interface ReviewDocument {
  bytes: Uint8Array;
  xml: XMLDocument;
  paragraphs: DocxParagraph[];
}
const children = (node: Element) => Array.from(node.children);
export async function openReviewDocument(bytes: Uint8Array): Promise<ReviewDocument> {
  if (bytes.length > 12 * 1024 * 1024) throw new Error("The file exceeds 12 MB.");
  const entries = readZipEntries(bytes);
  if ([...entries.keys()].some((name) => name.startsWith("_xmlsignatures/")))
    throw new Error("Signed Word documents cannot be edited here.");
  const entry = entries.get("word/document.xml");
  if (!entry) throw new Error("Select a Word .docx document.");
  const text = await readZipEntryText(bytes, entry, { maxBytes: MAX_XML });
  if (/<!DOCTYPE|<!ENTITY/i.test(text))
    throw new Error("This document's XML format is not supported.");
  const xml = new DOMParser().parseFromString(text, "application/xml");
  if (xml.getElementsByTagName("parsererror").length)
    throw new Error("The Word document could not be read.");
  const settings = entries.get("word/settings.xml");
  if (
    settings &&
    /documentProtection/.test(await readZipEntryText(bytes, settings, { maxBytes: MAX_XML }))
  )
    throw new Error("This document is protected. Edit it in Word.");
  const hasComplexFields = xml.getElementsByTagNameNS(WORD, "fldChar").length > 0;
  const protectedAncestor = (node: Element): boolean => {
    for (let parent = node.parentElement; parent; parent = parent.parentElement)
      if (
        parent.namespaceURI === WORD &&
        ["sdt", "del", "ins", "moveFrom", "moveTo"].includes(parent.localName)
      )
        return true;
    return false;
  };
  const paragraphs = Array.from(xml.getElementsByTagNameNS(WORD, "p")).map((p, index) => ({
    index,
    text: Array.from(p.getElementsByTagNameNS(WORD, "t"))
      .map((t) => t.textContent ?? "")
      .join(""),
    // Refuse complex paragraphs rather than flatten fields, links, comments or prior revisions.
    editable:
      !hasComplexFields &&
      !protectedAncestor(p) &&
      children(p).every(
        (n) =>
          n.namespaceURI === WORD &&
          (n.localName === "pPr" ||
            (n.localName === "r" &&
              children(n).every(
                (c) => c.namespaceURI === WORD && ["rPr", "t"].includes(c.localName),
              ))),
      ) &&
      !p.getElementsByTagNameNS(WORD, "sectPr").length,
  }));
  if (paragraphs.length > 10000)
    throw new Error("This document is too large for the review editor.");
  return { bytes, xml, paragraphs };
}
export function reviewChanges(doc: ReviewDocument, edits: ReadonlyMap<number, string>) {
  return doc.paragraphs
    .filter((p) => edits.has(p.index) && edits.get(p.index) !== p.text)
    .map((p) => ({ index: p.index, before: p.text, after: edits.get(p.index)! }));
}
/** Keep untouched ZIP bodies intact; encode edits as real Word insertion/deletion revisions. */
export function saveReviewedDocument(
  doc: ReviewDocument,
  edits: ReadonlyMap<number, string>,
  author: string,
  date = new Date(),
): Uint8Array {
  const changes = reviewChanges(doc, edits);
  if (!changes.length) return doc.bytes;
  const xml = doc.xml.cloneNode(true) as XMLDocument;
  const paragraphs = Array.from(xml.getElementsByTagNameNS(WORD, "p"));
  let nextId =
    Array.from(xml.getElementsByTagName("*")).reduce(
      (n, e) => Math.max(n, Number(e.getAttributeNS(WORD, "id")) || 0),
      0,
    ) + 1;
  if (!Number.isSafeInteger(nextId) || nextId + changes.length * 2 > 2147483647)
    throw new Error("This document has unsupported revision identifiers.");
  for (const change of changes) {
    if (!doc.paragraphs[change.index]?.editable)
      throw new Error("This paragraph contains Word features that must be edited in Word.");
    if (
      change.after.length > 100000 ||
      /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(change.after)
    )
      throw new Error("This paragraph contains unsupported text.");
    const p = paragraphs[change.index]!;
    const runs = children(p).filter((n) => n.localName === "r");
    const style = runs[0]?.getElementsByTagNameNS(WORD, "rPr")[0]?.cloneNode(true);
    const revision = (name: string) => {
      const element = xml.createElementNS(WORD, `w:${name}`);
      element.setAttributeNS(WORD, "w:id", String(nextId++));
      element.setAttributeNS(WORD, "w:author", author.slice(0, 200));
      element.setAttributeNS(WORD, "w:date", date.toISOString());
      return element;
    };
    const deleted = revision("del");
    for (const run of runs) {
      for (const text of Array.from(run.getElementsByTagNameNS(WORD, "t"))) {
        const replacement = xml.createElementNS(WORD, "w:delText");
        replacement.setAttribute("xml:space", "preserve");
        replacement.textContent = text.textContent;
        text.replaceWith(replacement);
      }
      deleted.appendChild(run);
    }
    if (runs.length) p.appendChild(deleted);
    if (change.after) {
      const inserted = revision("ins");
      const run = xml.createElementNS(WORD, "w:r");
      if (style) run.appendChild(style);
      change.after.split("\n").forEach((line, i) => {
        if (i) run.appendChild(xml.createElementNS(WORD, "w:br"));
        const text = xml.createElementNS(WORD, "w:t");
        text.setAttribute("xml:space", "preserve");
        text.textContent = line;
        run.appendChild(text);
      });
      inserted.appendChild(run);
      p.appendChild(inserted);
    }
  }
  const content = new TextEncoder().encode(new XMLSerializer().serializeToString(xml));
  return writeZip(
    [...readZipEntries(doc.bytes).values()].map((entry) =>
      entry.name === "word/document.xml"
        ? {
            name: entry.name,
            body: content,
            compressionMethod: 0,
            crc32: crc32(content),
            uncompressedSize: content.length,
          }
        : {
            name: entry.name,
            body: readZipEntryBody(doc.bytes, entry),
            compressionMethod: entry.compressionMethod,
            crc32: entry.crc32,
            uncompressedSize: entry.uncompressedSize,
          },
    ),
  );
}
